import { useState } from 'react';
import { addProfileRole, removeProfileRole } from '../../services/businessService';

const SHOP_ROLES = [
  { value: 'shop_owner', label: 'Shop Owner' },
  { value: 'shop_admin', label: 'Shop Admin' },
  { value: 'shop_user', label: 'Shop User' },
];

interface ManageRolesModalProps {
  businessId: string;
  profileId: string;
  currentRoles: string[];
  onClose: () => void;
  onSuccess: () => void;
}

export function ManageRolesModal({ businessId, profileId, currentRoles, onClose, onSuccess }: ManageRolesModalProps) {
  const existingShopRole = currentRoles.find((r) => SHOP_ROLES.some((sr) => sr.value === r)) || '';
  const isOwner = currentRoles.includes('owner');
  const isAdmin = currentRoles.includes('admin');
  const visibleRoles = isOwner
    ? SHOP_ROLES
    : isAdmin
      ? SHOP_ROLES.filter((r) => r.value !== 'shop_owner')
      : SHOP_ROLES.filter((r) => r.value === 'shop_user');
  const [selected, setSelected] = useState(existingShopRole);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selected === existingShopRole) {
      onClose();
      return;
    }

    setError('');
    setLoading(true);
    try {
      if (existingShopRole) {
        await removeProfileRole(businessId, profileId, existingShopRole);
      }
      if (selected) {
        await addProfileRole(businessId, profileId, selected);
      }
      onSuccess();
    } catch {
      setError('Failed to update roles');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-sm mx-4">
        <form onSubmit={handleSubmit}>
          <div className="px-6 pt-6 pb-4">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Manage Shop Roles</h2>
            <div className="space-y-3">
              {visibleRoles.map((r) => (
                <label key={r.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="shopRole"
                    value={r.value}
                    checked={selected === r.value}
                    onChange={() => {}}
                    onClick={() => setSelected((prev) => prev === r.value ? '' : r.value)}
                    className="border-gray-300"
                  />
                  {r.label}
                </label>
              ))}
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          </div>
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || selected === existingShopRole}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Applying...' : 'Apply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
